import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

const OPEN_DELAY_MS = 80;

/** Gap to the viewport edge, and between trigger and panel. */
const EDGE = 8;
const GAP = 6;

/**
 * A hover explanation that actually appears.
 *
 * The obvious way to write one is `title`, and it is the wrong way: the
 * browser's own tooltip waits about a second of complete stillness, dies on
 * the smallest pointer drift, truncates long text, is unreachable from the
 * keyboard, and on some platforms never paints at all. A page whose numbers
 * all carry their arithmetic in `title` therefore reads as if it had nothing
 * to say — the dotted underline promises intel that never arrives.
 *
 * The panel is portalled to `body` and positioned fixed because most triggers
 * sit inside tables that scroll horizontally, which would clip an absolutely
 * positioned child. Fixed panels do not follow their trigger, so any scroll
 * closes it.
 */
export function Hint({
  text,
  children,
  className,
  style,
  decorate = true,
}: {
  /** The explanation. Without one the children render bare. */
  text?: string | null;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** The dotted underline that says there is something to hover. */
  decorate?: boolean;
}) {
  const panelId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The trigger's rect at open time: it is both the "is open" flag and the
  // anchor the panel is measured against.
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const open = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setRect(r);
    }, OPEN_DELAY_MS);
  };

  const close = () => {
    clearTimeout(timer.current);
    setRect(null);
    setPos(null);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  // Runs before paint, so the panel is never seen at the provisional origin.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!rect || !panel) return;
    const p = panel.getBoundingClientRect();
    const left = Math.min(
      Math.max(EDGE, rect.left + rect.width / 2 - p.width / 2),
      Math.max(EDGE, window.innerWidth - p.width - EDGE)
    );
    const above = rect.top - p.height - GAP;
    setPos({ top: above >= EDGE ? above : rect.bottom + GAP, left });
  }, [rect]);

  useEffect(() => {
    if (!rect) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    // Capture: the scroll that matters is usually a table's, not the window's.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [rect]);

  if (!text) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={0}
        // Only while the panel exists: a describedby pointing at nothing is
        // read as an empty description by some screen readers.
        aria-describedby={rect ? panelId : undefined}
        style={style}
        onPointerEnter={open}
        onPointerLeave={close}
        onFocus={open}
        onBlur={close}
        className={cn(
          "cursor-help outline-none focus-visible:ring-1 focus-visible:ring-admin-card-header",
          decorate && "decoration-dotted underline underline-offset-2",
          className
        )}
      >
        {children}
      </span>
      {rect &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="tooltip"
            className={cn(
              "pointer-events-none fixed z-50 max-w-[22rem] rounded-md border border-admin-card-border bg-admin-card px-3 py-2 text-xs leading-snug text-admin-text-primary shadow-lg whitespace-pre-line",
              !pos && "opacity-0"
            )}
            style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}
